import { getCategory, getProgram } from '@/lib/programs';
import programsJson from '@/data/programs.json';

/** The five program families a quote can be browsed by; order is display order. */
export const TESTIMONIAL_CATEGORIES = [
  { slug: 'team-building', label: 'Team building' },
  { slug: 'charitable-csr', label: 'Charitable' },
  { slug: 'smarthunts', label: 'SmartHunts' },
  { slug: 'game-shows', label: 'Game shows' },
  { slug: 'professional-development', label: 'Professional development' },
] as const;

export type TestimonialCategory = (typeof TESTIMONIAL_CATEGORIES)[number]['slug'];

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

const programBySlugTitle = new Map(
  (Array.isArray(programsJson) ? programsJson : Object.values(programsJson as Record<string, unknown>))
    .filter((p): p is { slug: string; title: string } => typeof p === 'object' && p !== null && 'slug' in p && 'title' in p)
    .map((p) => [norm(p.title), p.slug]),
);

/**
 * program slug → category slug, from the catalog's category lists. A best
 * seller sits in the team-building list as well as its own family, so the
 * specific families are read first and team building last.
 */
const categoryOfProgram = new Map<string, TestimonialCategory>();
const SPECIFIC_FIRST = [...TESTIMONIAL_CATEGORIES].sort((a, b) => Number(a.slug === 'team-building') - Number(b.slug === 'team-building'));
for (const category of SPECIFIC_FIRST) {
  for (const slug of getCategory(category.slug)?.programSlugs ?? []) {
    if (!categoryOfProgram.has(slug)) categoryOfProgram.set(slug, category.slug);
  }
}

/**
 * Keyword rules for the quotes whose program name is a variant the catalog
 * does not spell the same way ("Game Show Feud", "Build-a-Wheelchair",
 * "Virtual Survey Says!"). Order matters: charitable names often contain
 * "SmartHunt" or "Minute 2 Win It", so they are tested first.
 */
const RULES: readonly [RegExp, TestimonialCategory][] = [
  [/wheelchair|bike|donat|charit|mini.?golf|guitar|science kits|green machine|build.?a.?computer|for charity/i, 'charitable-csr'],
  [/smarthunt|scavenger|hunt\b|escape|mystery of da vinci|house of blues|planet hollywood|dinner with the mob/i, 'smarthunts'],
  [/game show|feud|survey says|price it right|minute 2 win it|mania|jeopar|trivia/i, 'game-shows'],
  [/leader|workshop|disc\b|\bitp\b|igniting|strategic|emerging|training|communication/i, 'professional-development'],
];

const MINI_GOLF = 'mini-golf-build-and-food-donation';

/**
 * Spelling variants and retired names in the scraped quotes → the catalog
 * program that owns the page and the photo. Build-a-Wheelchair became
 * Build-A-Rollator; Price It Right became Guess That Price.
 */
export const PROGRAM_ALIASES: Readonly<Record<string, string>> = {
  buildawheelchair: 'build-a-rollator',
  wheelchair: 'build-a-rollator',
  rollator: 'build-a-rollator',
  gameshowfeud: 'best-corporate-feud',
  feud: 'best-corporate-feud',
  virtualfeud: 'virtual-best-corporate-feud',
  virtualpriceitright: 'virtual-guess-that-price',
  locateanddonate: 'locate-and-donate-smarthunt',
  locateanddonatesmarthunt: 'locate-and-donate-smarthunt',
  charityminigolfcoursebuild: MINI_GOLF,
  charityminigolfbuild: MINI_GOLF,
  charityminiaturegolfcoursebuild: MINI_GOLF,
  minigolf: MINI_GOLF,
  minigolfandfooddonation: MINI_GOLF,
  itp: 'igniting-team-performance',
  bikebuild: 'bike-build-donation',
  bike: 'bike-build-donation',
  buildbuilddonation: 'bike-build-donation',
  gokart: 'go-kart-competition',
  minute2winit: 'a-minute-2-win-it',
  aminute2winitrecordbreakers: 'a-minute-2-win-it',
  disc: 'disc-workshop',
  discprofileworkshop: 'disc-workshop',
  earthwindsolar: 'earth-wind-and-solar',
  ews: 'earth-wind-and-solar',
  guitar: 'build-a-guitar',
  donationnation: 'donation-nation-care-packs',
  corporatesurvivors: 'corporate-survivor',
  thecorporatecanvas: 'corporate-canvas',
  tapassangriamaking: 'tapas-and-sangria-making',
};

/** The catalog slug a quote's program name refers to, by exact title or alias; undefined when the program is not in the catalog. */
export function programSlugForTestimonial(program: string | undefined): string | undefined {
  if (!program) return undefined;
  const key = norm(program);
  return programBySlugTitle.get(key) ?? PROGRAM_ALIASES[key];
}

/** The category a quote belongs to, from its program name; team building when nothing more specific fits. */
export function categoryOfTestimonial(program: string | undefined): TestimonialCategory {
  if (!program) return 'team-building';
  const slug = programBySlugTitle.get(norm(program));
  if (slug) {
    const known = categoryOfProgram.get(slug) ?? categoryOfProgram.get(getProgram(slug)?.slug ?? '');
    if (known) return known;
  }
  for (const [pattern, category] of RULES) if (pattern.test(program)) return category;
  return 'team-building';
}
